/**
 * Authentication Synchronization Service
 * Synchronizes login/logout events across all browser tabs using BroadcastChannel API
 * with localStorage fallback for older browsers
 */

const SYNC_CHANNEL_NAME = 'auth_sync_channel';
const STORAGE_EVENT_KEY = 'auth_sync_event';

class AuthSyncService {
  constructor() {
    this.broadcastChannel = null;
    this.listeners = [];
    this.useBroadcastChannel = false;
    
    this.initChannel();
  }

  /**
   * Initialize the broadcast channel or setup storage listener
   */
  initChannel() {
    try {
      // Try to use BroadcastChannel API (modern approach, better performance)
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel(SYNC_CHANNEL_NAME);
        this.broadcastChannel.onmessage = (event) => {
          this.handleSyncEvent(event.data);
        };
        this.useBroadcastChannel = true;
        console.log('[AuthSync] Using BroadcastChannel API');
      } else {
        // Fallback to storage events for older browsers
        window.addEventListener('storage', (event) => {
          if (event.key === STORAGE_EVENT_KEY && event.newValue) {
            try {
              const data = JSON.parse(event.newValue);
              this.handleSyncEvent(data);
            } catch (e) {
              console.error('[AuthSync] Failed to parse storage event:', e);
            }
          }
        });
        console.log('[AuthSync] Using localStorage events (fallback)');
      }
    } catch (error) {
      console.warn('[AuthSync] Failed to initialize channel:', error);
      // Setup fallback
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_EVENT_KEY && event.newValue) {
          try {
            const data = JSON.parse(event.newValue);
            this.handleSyncEvent(data);
          } catch (e) {
            console.error('[AuthSync] Failed to parse storage event:', e);
          }
        }
      });
    }
  }

  /**
   * Handle incoming sync events from other tabs
   */
  handleSyncEvent(data) {
    if (!data || !data.type) return;

    console.log('[AuthSync] Received event from another tab:', data.type);

    // Notify all registered listeners
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('[AuthSync] Error in listener callback:', error);
      }
    });
  }

  /**
   * Register a listener for auth sync events
   * @param {Function} callback - Function to call when sync event occurs
   * @returns {Function} Unsubscribe function
   */
  onAuthEvent(callback) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Broadcast a login event to all tabs
   * @param {Object} userData - User data object
   */
  broadcastLogin(userData) {
    const event = {
      type: 'LOGIN',
      timestamp: Date.now(),
      userId: userData?.id,
      userData: userData
    };

    this.sendEvent(event);
    console.log('[AuthSync] Broadcasting LOGIN event');
  }

  /**
   * Broadcast a logout event to all tabs
   */
  broadcastLogout() {
    const event = {
      type: 'LOGOUT',
      timestamp: Date.now(),
      userId: null
    };

    this.sendEvent(event);
    console.log('[AuthSync] Broadcasting LOGOUT event');
  }

  /**
   * Send an event through the appropriate channel
   * @private
   */
  sendEvent(event) {
    if (this.useBroadcastChannel && this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
      } catch (error) {
        console.error('[AuthSync] Failed to send via BroadcastChannel:', error);
        this.fallbackToStorage(event);
      }
    } else {
      this.fallbackToStorage(event);
    }
  }

  /**
   * Fallback method using localStorage to send events
   * @private
   */
  fallbackToStorage(event) {
    try {
      // Add a unique identifier to trigger storage event even if data is same
      const eventWithId = {
        ...event,
        eventId: `${Date.now()}-${Math.random()}`
      };
      localStorage.setItem(STORAGE_EVENT_KEY, JSON.stringify(eventWithId));
      // Clean up after a short delay
      setTimeout(() => {
        if (localStorage.getItem(STORAGE_EVENT_KEY) === JSON.stringify(eventWithId)) {
          localStorage.removeItem(STORAGE_EVENT_KEY);
        }
      }, 1000);
    } catch (error) {
      console.error('[AuthSync] Failed to use localStorage fallback:', error);
    }
  }

  /**
   * Cleanup the sync service
   */
  destroy() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    this.listeners = [];
  }
}

// Create singleton instance
export const authSync = new AuthSyncService();

export default authSync;
